/**
 * Safe LocalStorage Utility
 * Handles parsing, validation, and error recovery for localStorage operations
 */

/**
 * Safely get and parse JSON from localStorage
 * @param {string} key - localStorage key
 * @param {*} defaultValue - Default value if key doesn't exist or parse fails
 * @param {Function} validator - Optional validation function that returns true if valid
 * @returns {*} Parsed value or defaultValue
 */
export const safeGetItem = (key, defaultValue = null, validator = null) => {
  try {
    const item = localStorage.getItem(key);
    
    // Return default if item doesn't exist
    if (item === null || item === undefined) {
      return defaultValue;
    }
    
    // Try to parse JSON
    const parsed = JSON.parse(item);
    
    // Run validator if provided
    if (validator && typeof validator === 'function') {
      if (!validator(parsed)) {
        console.warn(`Validation failed for localStorage key: ${key}`);
        return defaultValue;
      }
    }
    
    return parsed;
    
  } catch (error) {
    console.error(`Failed to parse localStorage item "${key}":`, error);
    // Optionally clear corrupted data
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error(`Failed to remove corrupted localStorage item "${key}"`);
    }
    return defaultValue;
  }
};

/**
 * Safely set JSON data to localStorage
 * @param {string} key - localStorage key
 * @param {*} value - Value to store (will be JSON stringified)
 * @returns {boolean} True if successful, false otherwise
 */
export const safeSetItem = (key, value) => {
  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error(`Failed to save to localStorage key "${key}":`, error);
    
    // Check if quota exceeded
    if (error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded. Consider clearing old data.');
    }
    
    return false;
  }
};

/**
 * Safely remove item from localStorage
 * @param {string} key - localStorage key
 * @returns {boolean} True if successful, false otherwise
 */
export const safeRemoveItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Failed to remove localStorage item "${key}":`, error);
    return false;
  }
};

/**
 * Safely clear all localStorage
 * @returns {boolean} True if successful, false otherwise
 */
export const safeClear = () => {
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
    return false;
  }
};

/**
 * Get user object from localStorage with validation
 * @returns {Object|null} User object or null if invalid
 */
export const getSafeUser = () => {
  return safeGetItem('user', null, (user) => {
    // Validate user object structure
    return user && 
           typeof user === 'object' && 
           typeof user.email === 'string' &&
           typeof user.fullname === 'string';
  });
};

/**
 * Get form data from localStorage with validation
 * @returns {Object|null} Form data or null if invalid
 */
export const getSafeFormData = () => {
  return safeGetItem('formData', null, (data) => {
    return data && typeof data === 'object';
  });
};


/**
 * Get array from localStorage with validation
 * @param {string} key - localStorage key
 * @param {Array} defaultValue - Default value if not found or invalid
 * @returns {Array} Array or defaultValue
 */
export const getSafeArray = (key, defaultValue = []) => {
  return safeGetItem(key, defaultValue, (data) => {
    return Array.isArray(data);
  });
};

/**
 * Get boolean from localStorage
 * @param {string} key - localStorage key
 * @param {boolean} defaultValue - Default value if not found
 * @returns {boolean} Boolean value
 */
export const getSafeBoolean = (key, defaultValue = false) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    
    // Handle string 'true'/'false'
    if (item === 'true') return true;
    if (item === 'false') return false;
    
    // Try parsing as JSON
    try {
      const parsed = JSON.parse(item);
      return Boolean(parsed);
    } catch {
      return defaultValue;
    }
  } catch (error) {
    console.error(`Failed to get boolean for key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Get string from localStorage
 * @param {string} key - localStorage key
 * @param {string} defaultValue - Default value if not found
 * @returns {string} String value
 */
export const getSafeString = (key, defaultValue = '') => {
  try {
    const item = localStorage.getItem(key);
    return item !== null ? String(item) : defaultValue;
  } catch (error) {
    console.error(`Failed to get string for key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Check if localStorage is available
 * @returns {boolean} True if localStorage is available
 */
export const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Get multiple items from localStorage safely
 * @param {Array<string>} keys - Array of localStorage keys
 * @param {Object} defaults - Object with default values for each key
 * @returns {Object} Object with values for each key
 */
export const getSafeMultiple = (keys, defaults = {}) => {
  const result = {};
  
  keys.forEach(key => {
    result[key] = safeGetItem(key, defaults[key] || null);
  });
  
  return result;
};

/**
 * Safe batch remove from localStorage
 * @param {Array<string>} keys - Array of keys to remove
 */
export const safeRemoveMultiple = (keys) => {
  keys.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove key "${key}":`, error);
    }
  });
};

/**
 * Migrate localStorage data with transformation
 * @param {string} key - localStorage key
 * @param {Function} transformer - Function to transform old data to new format
 * @param {*} defaultValue - Default value if migration fails
 */
export const migrateLocalStorageData = (key, transformer, defaultValue = null) => {
  try {
    const oldData = safeGetItem(key, null);
    if (oldData === null) return defaultValue;
    
    const newData = transformer(oldData);
    safeSetItem(key, newData);
    return newData;
  } catch (error) {
    console.error(`Failed to migrate localStorage key "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Sanitize string to prevent XSS
 * @param {string} str - String to sanitize
 * @returns {string} Sanitized string
 */
export const sanitizeString = (str) => {
  if (typeof str !== 'string') return '';
  
  // Basic XSS prevention: escape HTML entities
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Validate and sanitize user object
 * @param {Object} user - User object to validate
 * @returns {Object|null} Sanitized user object or null if invalid
 */
export const validateAndSanitizeUser = (user) => {
  if (!user || typeof user !== 'object') return null;
  
  const sanitized = {};
  
  // Validate and sanitize each field
  if (user.email && typeof user.email === 'string') {
    sanitized.email = sanitizeString(user.email);
  } else {
    return null; // Email is required
  }
  
  if (user.fullname && typeof user.fullname === 'string') {
    sanitized.fullname = sanitizeString(user.fullname);
  } else {
    return null; // Fullname is required
  }
  
  if (user.role && typeof user.role === 'string') {
    sanitized.role = sanitizeString(user.role);
  }
  
  if (user.userId) {
    sanitized.userId = sanitizeString(String(user.userId));
  }
  
  return sanitized;
};

export default {
  safeGetItem,
  safeSetItem,
  safeRemoveItem,
  safeClear,
  getSafeUser,
  getSafeFormData,
  getSafeArray,
  getSafeBoolean,
  getSafeString,
  isLocalStorageAvailable,
  getSafeMultiple,
  safeRemoveMultiple,
  migrateLocalStorageData,
  sanitizeString,
  validateAndSanitizeUser
};
